"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Mention from "@tiptap/extension-mention";
import type {
  SuggestionOptions,
  SuggestionProps,
  SuggestionKeyDownProps,
} from "@tiptap/suggestion";
import {
  Fragment,
  Slice,
  type Node as ProseNode,
  type Schema,
} from "@tiptap/pm/model";
import { cn } from "@/lib/utils";
import { listMembers } from "@/lib/api/generated";
import type { UserResponse } from "@/lib/api/model";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { useAuth } from "@/features/auth";
import { useMentionLabelLookup } from "./mention-resolver";

const MENTION_TOKEN_RE = /<@([0-9a-f-]{36})>/gi;

// Linear-style badge: matches the `linear` variant in
// frontend/src/components/ui/badge.tsx (size="xs"). Reused inline by the
// Tiptap mention node and by the read-only MentionText renderer.
export const MENTION_BADGE_CLASS = [
  "inline-flex w-fit h-fit shrink-0 items-center justify-center gap-1",
  "rounded-sm px-1 py-0.5 text-xs font-medium whitespace-nowrap align-baseline",
  "text-primary dark:text-[#83b9ff] border-none border-composition overflow-hidden relative",
  "before:from-(--gradient-color)/40 before:to-(--gradient-color)/5",
  "before:bg-linear-to-t before:h-full before:w-full before:absolute before:top-0 before:left-0",
  "before:transition-all before:z-0 before:duration-300 before:pointer-events-none",
].join(" ");

export const MENTION_BADGE_STYLE = "--gradient-color: #2C7EFF";

type MentionState = {
  rect: DOMRect;
  items: UserResponse[];
  loading: boolean;
  selectedIndex: number;
};

function memberDisplayName(m: UserResponse): string {
  const name = `${m.first_name ?? ""} ${m.last_name ?? ""}`.trim();
  return name || m.email;
}

function expandMentionTokens(
  fragment: Fragment,
  schema: Schema,
  getLabel: (id: string) => string | null,
): Fragment {
  const mentionType = schema.nodes.mention;
  if (!mentionType) return fragment;
  const out: ProseNode[] = [];
  fragment.forEach((child) => {
    if (child.isText && child.text) {
      const text = child.text;
      const re = new RegExp(MENTION_TOKEN_RE.source, "gi");
      let last = 0;
      let m: RegExpExecArray | null;
      let matched = false;
      while ((m = re.exec(text))) {
        matched = true;
        if (m.index > last) {
          out.push(schema.text(text.slice(last, m.index), child.marks));
        }
        const id = m[1].toLowerCase();
        out.push(
          mentionType.create({ id, label: getLabel(id) ?? id.slice(0, 8) }),
        );
        last = m.index + m[0].length;
      }
      if (!matched) {
        out.push(child);
        return;
      }
      if (last < text.length) {
        out.push(schema.text(text.slice(last), child.marks));
      }
    } else {
      const inner = expandMentionTokens(child.content, schema, getLabel);
      out.push(child.copy(inner));
    }
  });
  return Fragment.fromArray(out);
}

function buildInitialDoc(
  content: string,
  getLabel: (id: string) => string | null,
) {
  type Segment =
    | { type: "text"; value: string }
    | { type: "mention"; id: string };
  const segments: Segment[] = [];
  let last = 0;
  let match: RegExpExecArray | null;
  MENTION_TOKEN_RE.lastIndex = 0;
  while ((match = MENTION_TOKEN_RE.exec(content))) {
    if (match.index > last) {
      segments.push({ type: "text", value: content.slice(last, match.index) });
    }
    segments.push({ type: "mention", id: match[1] });
    last = match.index + match[0].length;
  }
  if (last < content.length) {
    segments.push({ type: "text", value: content.slice(last) });
  }

  const paragraphs: Array<{ type: "paragraph"; content: object[] }> = [];
  let current: object[] = [];

  const pushParagraph = () => {
    paragraphs.push({ type: "paragraph", content: current });
    current = [];
  };

  for (const seg of segments) {
    if (seg.type === "mention") {
      current.push({
        type: "mention",
        attrs: { id: seg.id, label: getLabel(seg.id) ?? seg.id.slice(0, 8) },
      });
    } else {
      const lines = seg.value.split("\n");
      lines.forEach((line, i) => {
        if (line) current.push({ type: "text", text: line });
        if (i < lines.length - 1) pushParagraph();
      });
    }
  }
  pushParagraph();
  if (paragraphs.length === 0)
    paragraphs.push({ type: "paragraph", content: [] });

  return { type: "doc", content: paragraphs };
}

export interface MentionEditorHandle {
  clear: () => void;
  focus: () => void;
}

export interface MentionEditorProps {
  initialContent?: string;
  placeholder?: string;
  onChange?: (text: string) => void;
  onSubmit?: (text: string) => void;
  onCancel?: () => void;
  autoFocus?: boolean;
  className?: string;
  disabled?: boolean;
  minHeight?: number;
  "aria-label"?: string;
}

export function MentionEditor({
  initialContent = "",
  placeholder,
  onChange,
  onSubmit,
  onCancel,
  autoFocus,
  className,
  disabled,
  minHeight,
  "aria-label": ariaLabel,
}: MentionEditorProps) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const [mentionState, setMentionState] = useState<MentionState | null>(null);
  const stateRef = useRef<MentionState | null>(null);
  stateRef.current = mentionState;
  const isMentionOpenRef = useRef(false);
  const commandRef = useRef<
    ((item: { id: string; label: string }) => void) | null
  >(null);
  const queryReqIdRef = useRef(0);

  const getLabel = useMentionLabelLookup();
  const getLabelRef = useRef(getLabel);
  getLabelRef.current = getLabel;
  const { user } = useAuth();
  const currentUserId = user?.id ?? null;
  const currentUserIdRef = useRef(currentUserId);
  currentUserIdRef.current = currentUserId;
  const initialContentRef = useRef(initialContent);
  // Keep ref in sync but do not re-create the editor when caller passes a new
  // `initialContent` after first mount; the editor is the source of truth.
  initialContentRef.current = initialContent;

  const onSubmitRef = useRef(onSubmit);
  onSubmitRef.current = onSubmit;
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const suggestion = useMemo<
    Omit<SuggestionOptions<UserResponse>, "editor">
  >(() => {
    return {
      char: "@",
      items: ({ query }: { query: string }) => {
        const reqId = ++queryReqIdRef.current;
        return new Promise<UserResponse[]>((resolve) => {
          listMembers({
            search: query,
            per_page: 8,
            include_suspended: false,
          })
            .then((res) => {
              // Drop stale resolutions silently — never resolve, so the
              // suggestion plugin only fires onUpdate/onStart for the
              // latest query and we keep the previous items + loading
              // state visible until the fresh fetch returns.
              if (reqId !== queryReqIdRef.current) return;
              const me = currentUserIdRef.current;
              const filtered = (res.members ?? []).filter(
                (m) => !me || m.id !== me,
              );
              resolve(filtered);
            })
            .catch(() => {
              if (reqId !== queryReqIdRef.current) return;
              resolve([]);
            });
        });
      },
      command: ({ editor, range, props }) => {
        editor
          .chain()
          .focus()
          .insertContentAt(range, [
            {
              type: "mention",
              attrs: { id: props.id, label: props.label },
            },
            { type: "text", text: " " },
          ])
          .run();
      },
      render: () => {
        return {
          onBeforeStart: (props: SuggestionProps<UserResponse>) => {
            const rect = props.clientRect?.();
            if (!rect) return;
            commandRef.current = (item) =>
              props.command({ id: item.id, label: item.label } as never);
            isMentionOpenRef.current = true;
            setMentionState({
              rect,
              items: [],
              loading: true,
              selectedIndex: 0,
            });
          },
          onStart: (props: SuggestionProps<UserResponse>) => {
            const rect = props.clientRect?.();
            if (!rect) return;
            commandRef.current = (item) =>
              props.command({ id: item.id, label: item.label } as never);
            isMentionOpenRef.current = true;
            setMentionState({
              rect,
              items: props.items,
              loading: false,
              selectedIndex: 0,
            });
          },
          onBeforeUpdate: (props: SuggestionProps<UserResponse>) => {
            const rect = props.clientRect?.();
            if (!rect) return;
            commandRef.current = (item) =>
              props.command({ id: item.id, label: item.label } as never);
            setMentionState((prev) => ({
              rect,
              items: prev?.items ?? [],
              loading: true,
              selectedIndex: prev?.selectedIndex ?? 0,
            }));
          },
          onUpdate: (props: SuggestionProps<UserResponse>) => {
            const rect = props.clientRect?.();
            if (!rect) return;
            commandRef.current = (item) =>
              props.command({ id: item.id, label: item.label } as never);
            setMentionState({
              rect,
              items: props.items,
              loading: false,
              selectedIndex: 0,
            });
          },
          onKeyDown: (props: SuggestionKeyDownProps) => {
            const state = stateRef.current;
            if (!state) return false;
            const len = state.items.length;
            if (props.event.key === "ArrowDown") {
              if (len > 0) {
                setMentionState({
                  ...state,
                  selectedIndex: (state.selectedIndex + 1) % len,
                });
              }
              return true;
            }
            if (props.event.key === "ArrowUp") {
              if (len > 0) {
                setMentionState({
                  ...state,
                  selectedIndex: (state.selectedIndex - 1 + len) % len,
                });
              }
              return true;
            }
            if (props.event.key === "Enter" || props.event.key === "Tab") {
              if (len > 0) {
                const item = state.items[state.selectedIndex];
                if (item) {
                  commandRef.current?.({
                    id: item.id,
                    label: memberDisplayName(item),
                  });
                }
              }
              // Always swallow Enter/Tab while the popover is open so we don't
              // accidentally submit the note or break out to the next field.
              return true;
            }
            if (props.event.key === "Escape") {
              isMentionOpenRef.current = false;
              setMentionState(null);
              return true;
            }
            return false;
          },
          onExit: () => {
            isMentionOpenRef.current = false;
            setMentionState(null);
          },
        };
      },
    };
  }, []);

  const editor = useEditor({
    immediatelyRender: false,
    autofocus: autoFocus ?? false,
    extensions: [
      StarterKit.configure({ heading: false, codeBlock: false }),
      Placeholder.configure({
        placeholder: placeholder || "Write a note...",
        showOnlyCurrent: false,
      }),
      Mention.configure({
        HTMLAttributes: {
          class: MENTION_BADGE_CLASS,
          style: MENTION_BADGE_STYLE,
          "data-mention": "true",
          "data-type": "mention",
        },
        renderText: ({ node }) => `<@${node.attrs.id}>`,
        renderHTML: ({ options, node }) => [
          "span",
          {
            ...options.HTMLAttributes,
            "data-id": node.attrs.id,
            "data-label": node.attrs.label ?? "",
          },
          `@${node.attrs.label ?? node.attrs.id}`,
        ],
        suggestion,
      }),
    ],
    content: buildInitialDoc(initialContentRef.current, getLabel),
    editable: !disabled,
    onUpdate: ({ editor: e }) => {
      onChangeRef.current?.(e.getText());
    },
    editorProps: {
      attributes: {
        class: cn(
          "outline-none px-3 py-2 text-sm leading-5 text-foreground",
          "[&_p]:m-0 [&_p]:text-sm [&_p]:leading-5",
          "[&_.is-editor-empty:first-child::before]:text-foreground/50",
          "[&_.is-editor-empty:first-child::before]:content-[attr(data-placeholder)]",
          "[&_.is-editor-empty:first-child::before]:float-left",
          "[&_.is-editor-empty:first-child::before]:h-0",
          "[&_.is-editor-empty:first-child::before]:pointer-events-none",
        ),
        style: minHeight ? `min-height: ${minHeight}px` : "",
        ...(ariaLabel ? { "aria-label": ariaLabel } : {}),
      },
      transformPasted(slice, view) {
        const expanded = expandMentionTokens(
          slice.content,
          view.state.schema,
          getLabelRef.current,
        );
        return new Slice(expanded, slice.openStart, slice.openEnd);
      },
      handleKeyDown(_view, event) {
        if (isMentionOpenRef.current) return false;
        if (event.key === "Enter" && !event.shiftKey) {
          event.preventDefault();
          const text = editor?.getText() ?? "";
          if (text.trim()) onSubmitRef.current?.(text);
          return true;
        }
        if (event.key === "Escape") {
          onCancelRef.current?.();
          return true;
        }
        return false;
      },
    },
  });

  useEffect(() => {
    if (editor) editor.setEditable(!disabled);
  }, [editor, disabled]);

  if (!editor) return null;

  return (
    <>
      <EditorContent editor={editor} className={className} />
      {mounted && mentionState && (
        <MentionPopover
          state={mentionState}
          onHover={(idx) =>
            setMentionState((prev) =>
              prev ? { ...prev, selectedIndex: idx } : prev,
            )
          }
          onSelect={(member) => {
            commandRef.current?.({
              id: member.id,
              label: memberDisplayName(member),
            });
          }}
        />
      )}
    </>
  );
}

function MentionPopover({
  state,
  onSelect,
  onHover,
}: {
  state: MentionState;
  onSelect: (m: UserResponse) => void;
  onHover: (index: number) => void;
}) {
  const top = state.rect.bottom + 6;
  const left = state.rect.left;

  const isEmpty = !state.loading && state.items.length === 0;

  return createPortal(
    <div
      data-portal-dropdown="true"
      className="fixed z-[60] min-w-[240px] max-w-[320px] max-h-[280px] overflow-y-auto overflow-x-hidden rounded-lg border border-border bg-surface-elevated shadow-[0px_8px_24px_0px_rgba(0,0,0,0.35)]"
      style={{ top, left, pointerEvents: "auto" }}
    >
      {isEmpty ? (
        <div className="px-3 py-2.5 text-xs text-foreground/50">
          No matching members
        </div>
      ) : state.loading ? (
        <ul className="flex flex-col py-1">
          {[0, 1, 2, 3].map((i) => (
            <MentionSkeletonRow key={i} />
          ))}
        </ul>
      ) : (
        <ul className="flex flex-col py-1">
          {state.items.map((m, idx) => {
            const isActive = idx === state.selectedIndex;
            const name = memberDisplayName(m);
            const initials =
              `${m.first_name?.[0] ?? ""}${m.last_name?.[0] ?? ""}`.toUpperCase();
            return (
              <li key={m.id}>
                <button
                  type="button"
                  onMouseEnter={() => onHover(idx)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onSelect(m);
                  }}
                  className={cn(
                    "flex w-full items-center gap-2 px-2.5 py-1.5 text-left transition-colors",
                    isActive ? "bg-contrast/8" : "hover:bg-contrast/5",
                  )}
                >
                  <Avatar size="sm" className="rounded-[3.375px]">
                    <AvatarImage src={m.avatar_url ?? undefined} alt="" />
                    <AvatarFallback className="bg-primary/10 dark:bg-[#0D111A] text-[10px] font-medium text-primary dark:text-[#2C7EFF]">
                      {initials || "?"}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex min-w-0 flex-1 flex-col leading-tight">
                    <span className="truncate text-sm font-medium text-foreground">
                      {name}
                    </span>
                    <span className="truncate text-[11px] text-foreground/50">
                      {m.email}
                    </span>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>,
    document.body,
  );
}

const SK_PULSE =
  "bg-contrast/6 shimmer shimmer-bg text-foreground";

function MentionSkeletonRow() {
  return (
    <li className="flex items-center gap-2 px-2.5 py-1.5">
      <div className={cn("size-6 shrink-0 rounded-[3.375px]", SK_PULSE)} />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className={cn("h-3 w-24 rounded", SK_PULSE)} />
        <div className={cn("h-2.5 w-32 rounded", SK_PULSE)} />
      </div>
    </li>
  );
}
